import {
  AiMessageAnswer,
  AiMessages,
  AiMessageStatusEnum,
  AiRunKindEnum,
  AiRunKindFilterEnum,
  MessageType
} from "__generated__/graphql"

export const ANSWER_LABEL_KEYS: Partial<Record<MessageType, string>> = {
  [MessageType.Comment]: "ai_logs_answer_comment",
  [MessageType.PrivateReply]: "ai_logs_answer_private_reply",
  [MessageType.Question]: "ai_logs_answer_question"
}

export const RUN_KIND_LABEL_KEYS: Record<AiRunKindEnum, string> = {
  [AiRunKindEnum.FollowUp]: "ai_logs_kind_follow_up",
  [AiRunKindEnum.AutoClose]: "ai_logs_kind_auto_close"
}

const KNOWN_RUN_OUTCOMES = new Set([
  "SENT",
  "MEMORY_GONE",
  "RESCHEDULED",
  "ALREADY_SENT",
  "CUSTOMER_WROTE",
  "TURN_IN_FLIGHT",
  "CONVERSATION_NOT_OPEN",
  "AI_NOT_JOINED",
  "DISABLED",
  "PROMPT_MISSING",
  "EMPTY",
  "FAILED",
  "CLOSED",
  "CLOSED_WITH_MESSAGE",
  "CLOSED_MESSAGE_EMPTY",
  "CLOSED_NO_CHANNEL",
  "CLOSED_MESSAGE_ALREADY_SENT",
  "SKIPPED_HUMAN_JOINED",
  "SKIPPED_AUTO_CLOSE_OFF",
  "SKIPPED_CONVERSATION_GONE"
])

export const RUN_KIND_FILTER_OPTIONS: ReadonlyArray<{
  value: AiRunKindFilterEnum
  labelKey: string
}> = [
  { value: AiRunKindFilterEnum.Turn, labelKey: "ai_logs_kind_turn" },
  {
    value: AiRunKindFilterEnum.FollowUp,
    labelKey: RUN_KIND_LABEL_KEYS[AiRunKindEnum.FollowUp]
  },
  {
    value: AiRunKindFilterEnum.AutoClose,
    labelKey: RUN_KIND_LABEL_KEYS[AiRunKindEnum.AutoClose]
  }
]

/** Same rule as qpien-ai's list filter: a TURN is a run with no kind. */
export function matchesRunKind(
  message: AiMessages,
  kind: AiRunKindFilterEnum
): boolean {
  if (kind === AiRunKindFilterEnum.Turn) return !message.kind
  return String(message.kind) === String(kind)
}

export type RunOutcomeTone = "success" | "skipped" | "failed" | "pending"

const SUCCESS_OUTCOMES = new Set(["SENT", "CLOSED", "CLOSED_WITH_MESSAGE"])
const FAILED_OUTCOMES = new Set(["FAILED", "PROMPT_MISSING"])

/** Anything neither done nor broken (customer wrote, agent took over, closed without the message) reads as skipped. */
export function runOutcomeTone(message: AiMessages): RunOutcomeTone {
  if (message.status === AiMessageStatusEnum.Failed) return "failed"
  if (!message.outcome) return "pending"
  if (FAILED_OUTCOMES.has(message.outcome)) return "failed"
  return SUCCESS_OUTCOMES.has(message.outcome) ? "success" : "skipped"
}

/** qpien-ai owns the outcome codes; one this panel does not know yet shows as the raw code. */
export function runOutcomeLabel(
  outcome: string | null | undefined,
  translate: (key: string) => string
): string {
  if (!outcome) return ""
  return KNOWN_RUN_OUTCOMES.has(outcome)
    ? translate(`ai_logs_outcome_${outcome.toLowerCase()}`)
    : outcome
}

export function runEventLabel(
  message: AiMessages,
  translate: (key: string) => string
): string {
  if (!message.kind) return ""
  const kind = translate(RUN_KIND_LABEL_KEYS[message.kind as AiRunKindEnum])
  const outcome = runOutcomeLabel(message.outcome, translate)
  return outcome ? `${kind} · ${outcome}` : kind
}

/**
 * A comment turn answers on two surfaces at once (public reply + auto private reply), so the
 * backend records every outgoing answer in `answers`. Runs created before that field existed
 * only carry the single `answer`, so fall back to it.
 */
export function getAnswers(message: AiMessages): AiMessageAnswer[] {
  // Bound to a local first: `src/__generated__` is eslint-ignored, so returning one of its values
  // straight out of the function trips @typescript-eslint/no-unsafe-return.
  const answers: AiMessageAnswer[] = message.answers ?? []
  if (answers.length) return answers
  if (!message.answer) return []
  return [{ content: message.answer }]
}
